import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import App from './App';
import MarketplaceLayout from './layouts/MarketplaceLayout';

// Loading fallback
function Loading() {
  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p className="mt-4 text-slate-600">Loading...</p>
      </div>
    </div>
  );
}

// Helper for lazy-loaded pages
function lazyPage(importFn: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazy(importFn);
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Public pages
// ═════════════════════════════════════════════════════════════════════════════
const Home = () => lazyPage(() => import('../app/page'));
const Login = () => lazyPage(() => import('../app/login/page'));
const Signup = () => lazyPage(() => import('../app/signup/page'));
const SignupInvite = () => lazyPage(() => import('../app/signup/[inviteId]/page'));
const MarketplaceInvite = () => lazyPage(() => import('../app/marketplace-invite/[inviteId]/page'));

// ═════════════════════════════════════════════════════════════════════════════
// Auth-required pages
// ═════════════════════════════════════════════════════════════════════════════
const Account = () => lazyPage(() => import('../app/account/page'));
const Cart = () => lazyPage(() => import('../app/cart/page'));
const Catalog = () => lazyPage(() => import('../app/catalog/page'));
const Orders = () => lazyPage(() => import('../app/orders/page'));
const Recipes = () => lazyPage(() => import('../app/recipes/page'));
const RecipeDetail = () => lazyPage(() => import('../app/recipe/[recipeId]/page'));
const Support = () => lazyPage(() => import('../app/support/page'));
const Returns = () => lazyPage(() => import('../app/returns/page'));

// ═════════════════════════════════════════════════════════════════════════════
// Router Definition
// ═════════════════════════════════════════════════════════════════════════════
export const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      {
        element: <MarketplaceLayout />,
        children: [
          // Public routes
          { index: true, element: <Home /> },
          { path: '/login', element: <Login /> },
          { path: '/signup', element: <Signup /> },
          { path: '/signup/:inviteId', element: <SignupInvite /> },
          { path: '/marketplace-invite/:inviteId', element: <MarketplaceInvite /> },

          // Auth-required routes (auth check handled within each page)
          { path: '/account', element: <Account /> },
          { path: '/cart', element: <Cart /> },
          { path: '/catalog', element: <Catalog /> },
          { path: '/orders', element: <Orders /> },
          { path: '/recipes', element: <Recipes /> },
          { path: '/recipe/:recipeId', element: <RecipeDetail /> },
          { path: '/support', element: <Support /> },
          { path: '/returns', element: <Returns /> },
        ],
      },

      // Catch-all: redirect to home
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
